/**
 * PDFGeneratorService - Generates audit-ready, branded PDFs from form responses
 *
 * Uses jsPDF to create professional PDFs with full practice branding,
 * provider credentials, client identification, and signature rendering.
 */

import jsPDF from 'jspdf';
import { FormResponse, FormFieldResponse } from './firebase/FirebaseFormResponseService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface PracticeInfo {
  practiceName: string;
  providerName: string;
  credentials: string; // e.g., "LPC, NCC"
  licenseNumber: string;
  npiNumber: string;
  phone: string;
  email: string;
  address: string; // Formatted single-line or multi-line
  logo?: string; // Base64 data URL
}

export interface SignatureData {
  type: 'typed' | 'drawn';
  data: string; // typed text or base64 canvas image
  signedAt: string;
}

export interface PDFGenerationOptions {
  formResponse: FormResponse;
  clientName: string;
  clientDOB?: string;
  practiceInfo: PracticeInfo;
  signatureData?: SignatureData;
  documentId?: string;
  // Legacy compat
  companyName?: string;
}

class PDFGeneratorService {
  private static instance: PDFGeneratorService;

  // Page dimensions
  private readonly PAGE_WIDTH = 210; // A4 width in mm
  private readonly PAGE_HEIGHT = 297; // A4 height in mm
  private readonly MARGIN = 20;
  private readonly LINE_HEIGHT = 7;

  public static getInstance(): PDFGeneratorService {
    if (!PDFGeneratorService.instance) {
      PDFGeneratorService.instance = new PDFGeneratorService();
    }
    return PDFGeneratorService.instance;
  }

  /**
   * Fetch practice info from provider's Firestore document
   */
  public static async fetchPracticeInfo(providerId: string): Promise<PracticeInfo> {
    try {
      const providerDoc = await getDoc(doc(db, 'providers', providerId));
      if (!providerDoc.exists()) {
        return PDFGeneratorService.getDefaultPracticeInfo();
      }

      const data = providerDoc.data();

      // Build provider name with credentials
      const firstName = data.firstName || '';
      const lastName = data.lastName || '';
      const providerName = `${firstName} ${lastName}`.trim() || 'Provider';

      // Build credentials string
      const credentials = data.licenseType || '';

      // Build formatted address
      const addressParts = [];
      if (data.officeAddress) addressParts.push(data.officeAddress);
      const address = addressParts.join(', ');

      return {
        practiceName: data.practiceName || 'Practice',
        providerName,
        credentials,
        licenseNumber: data.licenseNumber || '',
        npiNumber: data.npiNumber || '',
        phone: typeof data.phone === 'string' ? data.phone : (data.phone?.number || data.phone?.value || ''),
        email: typeof data.email === 'string' ? data.email : (data.email?.address || ''),
        address,
        logo: data.practiceLogo || undefined,
      };
    } catch (error) {
      console.error('Error fetching practice info:', error);
      return PDFGeneratorService.getDefaultPracticeInfo();
    }
  }

  private static getDefaultPracticeInfo(): PracticeInfo {
    return {
      practiceName: 'Practice',
      providerName: 'Provider',
      credentials: '',
      licenseNumber: '',
      npiNumber: '',
      phone: '',
      email: '',
      address: '',
    };
  }

  /**
   * Generate PDF from form response
   */
  public generateFormPDF(options: PDFGenerationOptions): Blob {
    const { formResponse, clientName, clientDOB, practiceInfo, signatureData, documentId } = options;

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    let yPosition = this.MARGIN;

    // Add branded header
    yPosition = this.addHeader(doc, formResponse.formTemplateName, practiceInfo, yPosition);

    // Add client information with DOB
    yPosition = this.addClientInfo(doc, clientName, clientDOB, formResponse.submittedAt, yPosition);

    // Add form responses
    yPosition = this.addFormResponses(doc, formResponse.responses, yPosition);

    // Add signature block if present
    if (signatureData) {
      yPosition = this.addSignatureBlock(doc, signatureData, yPosition);
    }

    // Add footer with document ID
    this.addFooter(doc, formResponse.submittedAt, formResponse.completionTimeMinutes, documentId);

    return doc.output('blob');
  }

  /**
   * Add branded PDF header with practice info and form title
   */
  private addHeader(pdfDoc: jsPDF, formTitle: string, practiceInfo: PracticeInfo, yPos: number): number {
    const contentWidth = this.PAGE_WIDTH - 2 * this.MARGIN;

    // Practice logo (top-left) if available
    let logoEndX = this.MARGIN;
    if (practiceInfo.logo) {
      try {
        pdfDoc.addImage(practiceInfo.logo, 'PNG', this.MARGIN, yPos - 5, 20, 20);
        logoEndX = this.MARGIN + 25;
      } catch {
        // Logo failed to load — skip it
      }
    }

    // Practice name (bold, 14pt)
    pdfDoc.setFontSize(14);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text(practiceInfo.practiceName, logoEndX, yPos);
    yPos += 5;

    // Address + phone (9pt, gray) — email excluded from header per clinical form standards
    pdfDoc.setFontSize(9);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.setTextColor(80, 80, 80);

    // Line 2: Address and phone on one line
    const headerContactParts = [];
    if (practiceInfo.address) headerContactParts.push(practiceInfo.address);
    if (practiceInfo.phone) headerContactParts.push(practiceInfo.phone);
    if (headerContactParts.length > 0) {
      pdfDoc.text(headerContactParts.join('  |  '), logoEndX, yPos);
      yPos += 4;
    }

    // Provider credentials line
    const credentialParts = [];
    if (practiceInfo.providerName) {
      let providerLine = practiceInfo.providerName;
      if (practiceInfo.credentials) providerLine += `, ${practiceInfo.credentials}`;
      credentialParts.push(providerLine);
    }
    if (practiceInfo.npiNumber) credentialParts.push(`NPI: ${practiceInfo.npiNumber}`);
    if (practiceInfo.licenseNumber) credentialParts.push(`License: ${practiceInfo.licenseNumber}`);

    if (credentialParts.length > 0) {
      pdfDoc.text(credentialParts.join('  |  '), logoEndX, yPos);
      yPos += 4;
    }

    pdfDoc.setTextColor(0, 0, 0);

    // Horizontal rule
    yPos += 3;
    pdfDoc.setLineWidth(0.5);
    pdfDoc.line(this.MARGIN, yPos, this.PAGE_WIDTH - this.MARGIN, yPos);
    yPos += 5;

    // Form title (centered, 16pt bold)
    pdfDoc.setFontSize(16);
    pdfDoc.setFont('helvetica', 'bold');
    const titleLines = pdfDoc.splitTextToSize(formTitle, contentWidth);
    titleLines.forEach((line: string) => {
      const titleWidth = pdfDoc.getTextWidth(line);
      const titleX = (this.PAGE_WIDTH - titleWidth) / 2;
      pdfDoc.text(line, titleX, yPos);
      yPos += 7;
    });

    // Thin line under title
    yPos += 2;
    pdfDoc.setLineWidth(0.3);
    pdfDoc.line(this.MARGIN, yPos, this.PAGE_WIDTH - this.MARGIN, yPos);

    return yPos + 8;
  }

  /**
   * Add client information section with DOB
   */
  private addClientInfo(pdfDoc: jsPDF, clientName: string, clientDOB: string | undefined, submittedAt: string | Date, yPos: number): number {
    pdfDoc.setFontSize(10);
    pdfDoc.setFont('helvetica', 'normal');

    const submissionDate = new Date(submittedAt);
    const formattedDate = submissionDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Client name
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Client Name: ', this.MARGIN, yPos);
    const nameX = this.MARGIN + pdfDoc.getTextWidth('Client Name: ');
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(clientName, nameX, yPos);
    yPos += this.LINE_HEIGHT;

    // Date of birth
    if (clientDOB) {
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Date of Birth: ', this.MARGIN, yPos);
      const dobX = this.MARGIN + pdfDoc.getTextWidth('Date of Birth: ');
      pdfDoc.setFont('helvetica', 'normal');
      // Format DOB as MM/DD/YYYY
      const dobParts = clientDOB.split('-');
      const formattedDOB = dobParts.length === 3
        ? `${dobParts[1]}/${dobParts[2]}/${dobParts[0]}`
        : clientDOB;
      pdfDoc.text(formattedDOB, dobX, yPos);
      yPos += this.LINE_HEIGHT;
    }

    // Submission date
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Submission Date: ', this.MARGIN, yPos);
    const dateX = this.MARGIN + pdfDoc.getTextWidth('Submission Date: ');
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(formattedDate, dateX, yPos);

    // Horizontal line
    yPos += 5;
    pdfDoc.setLineWidth(0.3);
    pdfDoc.line(this.MARGIN, yPos, this.PAGE_WIDTH - this.MARGIN, yPos);

    return yPos + 10;
  }

  /**
   * Add form responses
   */
  private addFormResponses(pdfDoc: jsPDF, responses: FormFieldResponse[], yPos: number): number {
    responses.forEach((response, index) => {
      // Check if we need a new page
      if (yPos > this.PAGE_HEIGHT - 40) {
        pdfDoc.addPage();
        yPos = this.MARGIN;
      }

      // Question number and text (bold)
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'bold');
      const questionText = `${index + 1}. ${response.questionText}`;

      // Wrap long questions
      const questionLines = pdfDoc.splitTextToSize(questionText, this.PAGE_WIDTH - 2 * this.MARGIN);
      questionLines.forEach((line: string) => {
        pdfDoc.text(line, this.MARGIN, yPos);
        yPos += this.LINE_HEIGHT;
      });

      // Answer (normal font, indented)
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setTextColor(60, 60, 60);
      const answerText = this.formatAnswer(response);
      const answerLines = pdfDoc.splitTextToSize(answerText, this.PAGE_WIDTH - 2 * this.MARGIN - 10);

      answerLines.forEach((line: string) => {
        if (yPos > this.PAGE_HEIGHT - 30) {
          pdfDoc.addPage();
          yPos = this.MARGIN;
        }
        pdfDoc.text(line, this.MARGIN + 10, yPos);
        yPos += this.LINE_HEIGHT;
      });

      // Reset text color
      pdfDoc.setTextColor(0, 0, 0);

      // Add spacing between questions
      yPos += 5;
    });

    return yPos;
  }

  /**
   * Format answer based on type
   */
  private formatAnswer(response: FormFieldResponse): string {
    if (response.answer === null || response.answer === undefined || response.answer === '') {
      return '(No answer provided)';
    }

    // Handle array answers (multiple choice)
    if (Array.isArray(response.answer)) {
      if (response.answer.length === 0) {
        return '(No selection made)';
      }
      return response.answer.join(', ');
    }

    // Handle boolean
    if (typeof response.answer === 'boolean') {
      return response.answer ? 'Yes' : 'No';
    }

    // Handle date
    if (response.questionType === 'date') {
      try {
        const date = new Date(response.answer);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return String(response.answer);
      }
    }

    // Default: convert to string
    return String(response.answer);
  }

  /**
   * Add signature block
   */
  private addSignatureBlock(pdfDoc: jsPDF, signature: SignatureData, yPos: number): number {
    // Check if we need a new page
    if (yPos > this.PAGE_HEIGHT - 60) {
      pdfDoc.addPage();
      yPos = this.MARGIN;
    }

    yPos += 5;

    // Signature label
    pdfDoc.setFontSize(10);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Signature:', this.MARGIN, yPos);
    yPos += this.LINE_HEIGHT;

    if (signature.type === 'typed') {
      // Typed signature — italic courier 14pt
      pdfDoc.setFont('courier', 'italic');
      pdfDoc.setFontSize(14);
      pdfDoc.text(signature.data, this.MARGIN + 5, yPos);
      yPos += 8;
    } else if (signature.type === 'drawn' && signature.data) {
      // Drawn signature — render as image
      try {
        pdfDoc.addImage(signature.data, 'PNG', this.MARGIN + 5, yPos - 5, 60, 20);
        yPos += 18;
      } catch {
        // Fallback if image fails
        pdfDoc.setFont('helvetica', 'italic');
        pdfDoc.setFontSize(10);
        pdfDoc.text('(Drawn signature on file)', this.MARGIN + 5, yPos);
        yPos += 8;
      }
    }

    // Signature line
    pdfDoc.setLineWidth(0.3);
    pdfDoc.line(this.MARGIN + 5, yPos, this.MARGIN + 80, yPos);
    yPos += 4;

    // Signed date
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.setFontSize(9);
    pdfDoc.setTextColor(80, 80, 80);
    const signedDate = new Date(signature.signedAt).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    pdfDoc.text(`Signed: ${signedDate}`, this.MARGIN + 5, yPos);
    pdfDoc.setTextColor(0, 0, 0);

    return yPos + 10;
  }

  /**
   * Add footer with metadata and document ID
   */
  private addFooter(pdfDoc: jsPDF, submittedAt: string | Date, completionTime?: number, documentId?: string): void {
    const pageCount = pdfDoc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      pdfDoc.setPage(i);

      // Page number (centered)
      pdfDoc.setFontSize(8);
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setTextColor(128, 128, 128);

      const pageText = `Page ${i} of ${pageCount}`;
      const pageWidth = pdfDoc.getTextWidth(pageText);
      pdfDoc.text(pageText, (this.PAGE_WIDTH - pageWidth) / 2, this.PAGE_HEIGHT - 10);

      // Metadata (bottom left - only on last page)
      if (i === pageCount) {
        const metadata = [];
        metadata.push(`Generated: ${new Date().toLocaleString('en-US')}`);
        if (completionTime) {
          metadata.push(`Completion Time: ${completionTime} minutes`);
        }
        const docId = documentId || submittedAt;
        metadata.push(`Document ID: ${docId}`);

        let metaY = this.PAGE_HEIGHT - 22;
        metadata.forEach(line => {
          pdfDoc.text(line, this.MARGIN, metaY);
          metaY += 4;
        });
      }

      // Reset color
      pdfDoc.setTextColor(0, 0, 0);
    }
  }

  /**
   * Generate transcript PDF from session recording data
   */
  public generateTranscriptPDF(options: {
    patientName: string;
    providerName: string;
    sessionDate: Date;
    duration: number; // seconds
    speakerCount: number;
    utterances?: Array<{ speaker: string; text: string }>;
    diarizedText?: string;
    highlights?: Array<{ text: string }>;
    practiceInfo: PracticeInfo;
  }): Blob {
    const { patientName, providerName, sessionDate, duration, speakerCount, utterances, diarizedText, highlights, practiceInfo } = options;

    const pdfDoc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    let yPos = this.MARGIN;
    const contentWidth = this.PAGE_WIDTH - 2 * this.MARGIN;

    // Header with practice branding
    yPos = this.addHeader(pdfDoc, 'Session Transcript', practiceInfo, yPos);

    // Session info
    pdfDoc.setFontSize(10);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Client: ', this.MARGIN, yPos);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(patientName, this.MARGIN + pdfDoc.getTextWidth('Client: '), yPos);
    yPos += this.LINE_HEIGHT;

    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Provider: ', this.MARGIN, yPos);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(providerName, this.MARGIN + pdfDoc.getTextWidth('Provider: '), yPos);
    yPos += this.LINE_HEIGHT;

    const dateStr = sessionDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Session Date: ', this.MARGIN, yPos);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(dateStr, this.MARGIN + pdfDoc.getTextWidth('Session Date: '), yPos);
    yPos += this.LINE_HEIGHT;

    const durationMin = Math.round(duration / 60);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Duration: ', this.MARGIN, yPos);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(`${durationMin} minutes | ${speakerCount} speaker(s)`, this.MARGIN + pdfDoc.getTextWidth('Duration: '), yPos);
    yPos += 5;

    // Divider
    pdfDoc.setLineWidth(0.3);
    pdfDoc.line(this.MARGIN, yPos, this.PAGE_WIDTH - this.MARGIN, yPos);
    yPos += 8;

    // Key highlights
    if (highlights && highlights.length > 0) {
      pdfDoc.setFontSize(11);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Key Highlights', this.MARGIN, yPos);
      yPos += 6;
      pdfDoc.setFontSize(9);
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setTextColor(80, 80, 80);
      const highlightText = highlights.slice(0, 10).map(h => h.text).join(' | ');
      const highlightLines = pdfDoc.splitTextToSize(highlightText, contentWidth);
      highlightLines.forEach((line: string) => {
        pdfDoc.text(line, this.MARGIN, yPos);
        yPos += 5;
      });
      pdfDoc.setTextColor(0, 0, 0);
      yPos += 5;
    }

    // Transcript content
    pdfDoc.setFontSize(11);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Transcript', this.MARGIN, yPos);
    yPos += 8;

    pdfDoc.setFontSize(10);
    pdfDoc.setFont('helvetica', 'normal');

    if (utterances && utterances.length > 0) {
      utterances.forEach((u) => {
        if (yPos > this.PAGE_HEIGHT - 30) {
          pdfDoc.addPage();
          yPos = this.MARGIN;
        }

        const speaker = u.speaker === 'A' ? 'Provider' : 'Client';
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.setTextColor(u.speaker === 'A' ? 16 : 59, u.speaker === 'A' ? 100 : 130, u.speaker === 'A' ? 80 : 246);
        pdfDoc.text(`[${speaker}]`, this.MARGIN, yPos);
        const speakerWidth = pdfDoc.getTextWidth(`[${speaker}] `);

        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.setTextColor(0, 0, 0);
        const textLines = pdfDoc.splitTextToSize(u.text, contentWidth - speakerWidth - 5);
        textLines.forEach((line: string, idx: number) => {
          if (yPos > this.PAGE_HEIGHT - 30) {
            pdfDoc.addPage();
            yPos = this.MARGIN;
          }
          pdfDoc.text(line, idx === 0 ? this.MARGIN + speakerWidth : this.MARGIN + speakerWidth, yPos);
          yPos += 5;
        });
        yPos += 2;
      });
    } else if (diarizedText) {
      const lines = pdfDoc.splitTextToSize(diarizedText, contentWidth);
      lines.forEach((line: string) => {
        if (yPos > this.PAGE_HEIGHT - 30) {
          pdfDoc.addPage();
          yPos = this.MARGIN;
        }
        pdfDoc.text(line, this.MARGIN, yPos);
        yPos += 5;
      });
    }

    // Footer
    const pageCount = pdfDoc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdfDoc.setPage(i);
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(128, 128, 128);
      pdfDoc.text(`Page ${i} of ${pageCount}`, this.PAGE_WIDTH / 2 - 10, this.PAGE_HEIGHT - 10);
      if (i === pageCount) {
        pdfDoc.text(`Generated: ${new Date().toLocaleString('en-US')}`, this.MARGIN, this.PAGE_HEIGHT - 18);
        pdfDoc.text('CONFIDENTIAL — Protected Health Information (HIPAA)', this.MARGIN, this.PAGE_HEIGHT - 14);
      }
      pdfDoc.setTextColor(0, 0, 0);
    }

    return pdfDoc.output('blob');
  }

  /**
   * Generate filename for PDF
   */
  public generateFilename(
    clientName: string,
    dateOfBirth: string,
    formName: string,
    submissionDate: Date = new Date()
  ): string {
    const FileNamingService = require('./FileNamingService').default;
    const namingService = FileNamingService.getInstance();

    return namingService.generateFormFilename({
      clientName,
      dateOfBirth,
      formName,
      submissionDate,
      fileExtension: 'pdf'
    });
  }

  /**
   * Download PDF to user's device
   */
  public downloadPDF(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Open PDF in new tab (for preview)
   */
  public previewPDF(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  /**
   * Generate Clinical Note PDF with customizable header
   * Option A: Provider can customize practice name, provider name, address, date for contract work
   */
  public generateClinicalNotePDF(options: {
    template: string; // SOAP, DAP, BIRP, etc.
    sections: Record<string, string>; // { subjective: '...', objective: '...', ... }
    sectionLabels: { key: string; label: string }[]; // ordered section definitions
    patientName: string;
    sessionDate: string; // formatted date string
    status: 'draft' | 'signed';
    signedAt?: string;
    version?: number;
    // Customizable header fields (defaults to practice info if not provided)
    headerOverride?: {
      practiceName?: string;
      providerName?: string;
      credentials?: string;
      address?: string;
      phone?: string;
      npiNumber?: string;
      licenseNumber?: string;
      dateOfService?: string;
    };
    practiceInfo: PracticeInfo;
  }): Blob {
    const {
      template, sections, sectionLabels, patientName,
      sessionDate, status, signedAt, version,
      headerOverride, practiceInfo
    } = options;

    const pdfDoc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    let y = this.MARGIN;

    // Use overrides if provided, otherwise default practice info
    const header = {
      practiceName: headerOverride?.practiceName || practiceInfo.practiceName,
      providerName: headerOverride?.providerName || practiceInfo.providerName,
      credentials: headerOverride?.credentials || practiceInfo.credentials,
      address: headerOverride?.address || practiceInfo.address,
      phone: headerOverride?.phone || practiceInfo.phone,
      npiNumber: headerOverride?.npiNumber || practiceInfo.npiNumber,
      licenseNumber: headerOverride?.licenseNumber || practiceInfo.licenseNumber,
    };
    const dateOfService = headerOverride?.dateOfService || sessionDate;

    // --- HEADER ---
    pdfDoc.setFontSize(14);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text(header.practiceName, this.MARGIN, y);
    y += 5;

    pdfDoc.setFontSize(9);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.setTextColor(80, 80, 80);

    const contactParts = [];
    if (header.address) contactParts.push(header.address);
    if (header.phone) contactParts.push(header.phone);
    if (contactParts.length > 0) {
      pdfDoc.text(contactParts.join('  |  '), this.MARGIN, y);
      y += 4;
    }

    const credParts = [];
    if (header.providerName) {
      let line = header.providerName;
      if (header.credentials) line += `, ${header.credentials}`;
      credParts.push(line);
    }
    if (header.npiNumber) credParts.push(`NPI: ${header.npiNumber}`);
    if (header.licenseNumber) credParts.push(`License: ${header.licenseNumber}`);
    if (credParts.length > 0) {
      pdfDoc.text(credParts.join('  |  '), this.MARGIN, y);
      y += 4;
    }
    pdfDoc.setTextColor(0, 0, 0);

    // Horizontal rule
    y += 3;
    pdfDoc.setLineWidth(0.5);
    pdfDoc.line(this.MARGIN, y, this.PAGE_WIDTH - this.MARGIN, y);
    y += 8;

    // --- TITLE ---
    pdfDoc.setFontSize(16);
    pdfDoc.setFont('helvetica', 'bold');
    const title = `${template} Note`;
    const titleWidth = pdfDoc.getTextWidth(title);
    pdfDoc.text(title, (this.PAGE_WIDTH - titleWidth) / 2, y);
    y += 8;

    // Status badge
    pdfDoc.setFontSize(10);
    if (status === 'signed') {
      pdfDoc.setTextColor(16, 185, 129);
      pdfDoc.text('SIGNED', (this.PAGE_WIDTH - pdfDoc.getTextWidth('SIGNED')) / 2, y);
    } else {
      pdfDoc.setTextColor(234, 179, 8);
      pdfDoc.text('DRAFT — Provider Review Required', (this.PAGE_WIDTH - pdfDoc.getTextWidth('DRAFT — Provider Review Required')) / 2, y);
    }
    pdfDoc.setTextColor(0, 0, 0);
    y += 6;

    // --- CLIENT INFO ---
    pdfDoc.setFontSize(10);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.setTextColor(60, 60, 60);
    pdfDoc.text(`Patient: ${patientName}`, this.MARGIN, y);
    pdfDoc.text(`Date of Service: ${dateOfService}`, this.PAGE_WIDTH / 2, y);
    y += 5;
    if (version) {
      pdfDoc.text(`Version: v${version}`, this.MARGIN, y);
      y += 5;
    }
    pdfDoc.setTextColor(0, 0, 0);

    // Thin rule
    y += 2;
    pdfDoc.setLineWidth(0.2);
    pdfDoc.line(this.MARGIN, y, this.PAGE_WIDTH - this.MARGIN, y);
    y += 8;

    // --- SECTIONS ---
    const contentWidth = this.PAGE_WIDTH - 2 * this.MARGIN;

    for (const section of sectionLabels) {
      const text = sections[section.key];
      if (!text) continue;

      // Check page break
      if (y > this.PAGE_HEIGHT - 40) {
        pdfDoc.addPage();
        y = this.MARGIN;
      }

      // Section header
      pdfDoc.setFontSize(11);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text(section.label, this.MARGIN, y);
      y += 6;

      // Section body
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'normal');
      const lines = pdfDoc.splitTextToSize(text, contentWidth);
      for (const line of lines) {
        if (y > this.PAGE_HEIGHT - 20) {
          pdfDoc.addPage();
          y = this.MARGIN;
        }
        pdfDoc.text(line, this.MARGIN, y);
        y += 5;
      }
      y += 4;
    }

    // --- SIGNATURE ---
    if (status === 'signed' && signedAt) {
      if (y > this.PAGE_HEIGHT - 40) {
        pdfDoc.addPage();
        y = this.MARGIN;
      }
      y += 5;
      pdfDoc.setLineWidth(0.3);
      pdfDoc.line(this.MARGIN, y, this.MARGIN + 80, y);
      y += 5;
      pdfDoc.setFontSize(9);
      pdfDoc.setTextColor(16, 185, 129);
      pdfDoc.text(`Signed on ${signedAt}`, this.MARGIN, y);
      pdfDoc.setTextColor(0, 0, 0);
    }

    // --- FOOTER ---
    const pageCount = pdfDoc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdfDoc.setPage(i);
      pdfDoc.setFontSize(8);
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setTextColor(128, 128, 128);
      pdfDoc.text(`Page ${i} of ${pageCount}`, (this.PAGE_WIDTH - pdfDoc.getTextWidth(`Page ${i} of ${pageCount}`)) / 2, this.PAGE_HEIGHT - 10);
      pdfDoc.text('Clinical notes are provider-only and are NOT visible to clients (HIPAA protected).', this.MARGIN, this.PAGE_HEIGHT - 15);
      pdfDoc.setTextColor(0, 0, 0);
    }

    return pdfDoc.output('blob');
  }
}

export default PDFGeneratorService;
